import { useEffect, useState } from "react";
import { Loader2, Save, RefreshCw, Star, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/ui";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#0A0B0D] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none focus:border-[#FFB800]";

const Hint = ({ children }) => (
  <p className="mt-1.5 flex items-start gap-1.5 text-xs text-zinc-500">
    <Info size={12} className="mt-0.5 shrink-0 text-[#FFB800]/70" />
    {children}
  </p>
);

export default function IntegrationsPage() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const load = () =>
    api.get("/integrations").then((r) => {
      setForm({
        google_places_key: r.data.google_places_key || "",
        meta_pixel_id: r.data.meta_pixel_id || "",
        google_ads_id: r.data.google_ads_id || "",
        google_ads_label: r.data.google_ads_label || "",
      });
      setSyncStatus({
        last: r.data.last_review_sync,
        rating: r.data.google_rating,
        total: r.data.google_total_reviews,
      });
    }).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/integrations", form);
      toast.success("Integrações salvas! Já valem no site.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/sync-reviews");
      if (data.ok) {
        toast.success(`${data.added} nova(s) avaliação(ões) importada(s), ${data.updated} atualizada(s). Nota ${data.google_rating} (${data.google_total_reviews} avaliações no Google)`);
      } else {
        toast.error(data.error);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSyncing(false);
    }
  };

  if (!form) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="integrations-page">
      <PageHeader title="Integrações" sub="Pixel, anúncios e avaliações automáticas do Google" testId="integrations-header">
        <button
          onClick={save}
          disabled={saving}
          data-testid="integrations-save-button"
          className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105 disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
          {saving ? "Salvando..." : "Salvar Tudo"}
        </button>
      </PageHeader>

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="google-reviews-card">
          <h3 className="font-display text-lg font-bold uppercase text-white">
            Avaliações <span className="italic text-[#FFB800]">automáticas do Google</span>
          </h3>
          <p className="mt-1 text-xs text-zinc-500">
            Quando alguém comenta no seu Google Meu Negócio, a avaliação entra sozinha no site (sincroniza a cada 12h).
          </p>
          <div className="mt-5">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Chave do Google Places API</label>
            <input
              type="password"
              data-testid="google-places-key-input"
              placeholder="Cole a chave aqui (AIza...)"
              value={form.google_places_key}
              onChange={(e) => setForm({ ...form, google_places_key: e.target.value })}
              className={`${inputCls} mt-2`}
            />
            <Hint>
              console.cloud.google.com → ative "Places API (New)" → Credenciais → Criar chave de API. Depois salve e clique em "Sincronizar agora".
            </Hint>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button
              onClick={sync}
              disabled={syncing}
              data-testid="sync-reviews-button"
              className="flex items-center gap-2 rounded-full border border-[#FFB800]/50 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-[#FFB800] transition-colors hover:bg-[#FFB800]/10 disabled:opacity-60"
            >
              <RefreshCw size={15} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Sincronizando..." : "Sincronizar agora"}
            </button>
            {syncStatus?.rating && (
              <span className="flex items-center gap-1.5 text-sm text-zinc-300" data-testid="google-rating-status">
                <Star size={15} className="fill-[#FFB800] text-[#FFB800]" />
                {syncStatus.rating} · {syncStatus.total} avaliações no Google
              </span>
            )}
          </div>
          {syncStatus?.last && (
            <p className="mt-3 text-[11px] text-zinc-600">
              Última sincronização: {new Date(syncStatus.last).toLocaleString("pt-BR")}
            </p>
          )}
        </div>

        <div className="rounded-2xl border border-white/5 bg-[#11141A] p-6" data-testid="ads-tracking-card">
          <h3 className="font-display text-lg font-bold uppercase text-white">
            Pixel do Facebook <span className="italic text-[#FFB800]">+ Google Ads</span>
          </h3>
          <p className="mt-1 text-xs text-zinc-500">
            Rastreia visitas e conversões (cliques no WhatsApp e envios de formulário viram "Lead" nos dois).
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">ID do Pixel (Meta)</label>
              <input
                data-testid="meta-pixel-input"
                placeholder="Ex: 123456789012345"
                value={form.meta_pixel_id}
                onChange={(e) => setForm({ ...form, meta_pixel_id: e.target.value })}
                className={`${inputCls} mt-2`}
              />
              <Hint>Meta Events Manager → Fontes de dados → seu Pixel → copie o ID numérico.</Hint>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Tag do Google Ads</label>
              <input
                data-testid="google-ads-id-input"
                placeholder="Ex: AW-123456789"
                value={form.google_ads_id}
                onChange={(e) => setForm({ ...form, google_ads_id: e.target.value })}
                className={`${inputCls} mt-2`}
              />
              <Hint>Google Ads → Ferramentas → Conversões → sua ação de conversão → Tag do Google.</Hint>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-400">Rótulo de conversão do Google Ads</label>
              <input
                data-testid="google-ads-label-input"
                placeholder="Ex: AbC-D_efG-h12_34"
                value={form.google_ads_label}
                onChange={(e) => setForm({ ...form, google_ads_label: e.target.value })}
                className={`${inputCls} mt-2`}
              />
              <Hint>Fica na mesma tela da tag, no snippet do evento: send_to 'AW-xxx/RÓTULO'.</Hint>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-5 rounded-2xl border border-dashed border-white/10 bg-[#11141A]/60 p-6" data-testid="ads-accounts-note">
        <h3 className="font-display text-base font-bold uppercase text-zinc-300">
          Vinculação de contas de anúncios <span className="text-zinc-500">(próxima etapa)</span>
        </h3>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-zinc-500">
          Para ver gastos, impressões, cliques e leads das campanhas direto neste dashboard, é preciso criar um app no
          Meta for Developers e um projeto no Google Cloud com token de desenvolvedor do Google Ads. Quando quiser
          ativar, me avise que eu te passo o passo a passo e conecto tudo aqui.
        </p>
      </div>
    </div>
  );
}
